import { useCallback } from "react";
import Auth from "../../../Auth";
import { makeId } from "../../../Utils";
import { DEFAULT_CARD_SIZE } from "../Card";
import { CARD_PADDING, MAX_CARDS, TOP_EDGE } from "../constants";
import { OUTLINE_CARD_TYPE, TEXT_CARD_TYPE } from "../../../../../src/shared/canvasConstants";
import { ADD_CARD_ACTION, ADD_OUTLINE_GROUP_ACTION, SELECT_ACTION, SET_DATA_ACTION } from "./useCanvasReducer";
import { useToast } from "../../ToastWrapper";

export default ({
  setHasInteractedWithCanvas,
  cards,
  groups,
  flushSelectionCache,
  getHighestIndex,
  position,
  zoom,
  deleteCards,
  cardIdsString,
  groupIdsString,
  subscriptionJSON,
  canvasId,
  dispatch,
}) => {
  const { setToast } = useToast();

  /**
   * Returns center of the viewport
   */
  const center = ({ width, height }) => {
    const x = Math.round(position.x + (window.innerWidth / 2) * (1 / zoom) - width / 2);
    const y = Math.round(position.y + (window.innerHeight / 2) * (1 / zoom) - height / 2 - TOP_EDGE);

    return [x, y];
  };

  const addCard = useCallback((data) => {
    // Enforce max
    if (Object.keys(cards).length > MAX_CARDS) {
      setToast(`Cannot create card. There is a max of ${MAX_CARDS} per canvas.`);
      return false;
    }

    data.id = makeId(6);
    data.width = data.width || DEFAULT_CARD_SIZE[0];
    data.height = data.height || DEFAULT_CARD_SIZE[1];

    // if x or y not specified, assume center
    if (!data.x || !data.y) {
      const c = center({ width: data.width, height: data.height });
      data.x = c[0];
      data.y = c[1];
    }

    dispatch({
      type: ADD_CARD_ACTION,
      data: data,
    });

    flushSelectionCache();
    setHasInteractedWithCanvas(true);

    return data.id;
  }, [cards]);

  const addOutlineGroup = useCallback(
    ({ title = "Outline" } = {}) => {
      // first add the cards

      dispatch({
        type: ADD_OUTLINE_GROUP_ACTION,
        title: title,
        center: center({
          width: DEFAULT_CARD_SIZE[0],
          height: DEFAULT_CARD_SIZE[1],
        }),
      });
    },
    [cardIdsString, groupIdsString, position]
  );

  const groupFromCard = useCallback(
    (id) => {
      let out = null;
      Object.keys(groups).find((groupId) => {
        const group = groups[groupId];

        const found = group.cards.find((cardId) => cardId === id);
        if (found) {
          out = group;
        }
      });

      return out;
    },
    [groupIdsString, groups]
  );

  const addGeneratedCard = useCallback(
    /**
     * Generates a new card based on this card
     * @param {Object} params
     * @param {Object} params.id id of card
     * @param {Object} params.x x to place first card
     * @param {Object} params.y y to place first card
     * @param {Object} params.width width of generated cards to place
     * @param {Object} params.height height of generated cards to place
     * @param {Object} params.sourceCardIds source card ids to set
     * @param {Object} params.n how many cards to generate
     * @returns
     */
    ({
      x, //
      y,
      ids,
      width,
      height,
      sourceCardIds,
      n = 3,
    }) => {
      if (!subscriptionJSON.hasAccessToFeatures) {
        return setToast("You must have an active subscription or trial to use AI features.");
      }

      if (!ids || ids.length === 0) {
        throw new Error("Missing or empty ids");
      }

      // construct data based on card type and number of cards

      let url;
      const data = {
        canvasId: canvasId,
        n: n,
      };

      if (ids.length > 1) {
        // for more than one card, we use the canvasCard endpoint
        url = "/api/ai/canvasCard";

        const prompt = ids
          .filter((id) => [TEXT_CARD_TYPE, OUTLINE_CARD_TYPE].includes(cards[id].type) && cards[id].content.trim().length > 0)
          .map((id) => {
            return cards[id].content;
          });

        if (prompt.length === 0) {
          return setToast("There must be at least one non-empty card to generate more");
        }
        data.prompt = prompt;
      } else {
        const card = cards[ids[0]];

        switch (card.type) {
          case OUTLINE_CARD_TYPE:
            url = "/api/ai/canvasOutlineCard";

            // construct prompt from the group
            const group = groupFromCard(ids[0]);
            if (group) {
              console.log(group);

              const outline = {};
              const headerCard = cards[group.cards[0]];

              if (!headerCard.content) {
                return setToast("Please fill out the outline concept before generating a new outline card");
              }

              const concept = headerCard.content;
              const beatId = card.data.beatId;

              group.cards.slice(1).forEach((cardId, i) => {
                const card = cards[cardId];
                outline[card.data.beatId] = card.content;
              });

              console.log(outline);
              console.log(concept);
              console.log(beatId);

              data.outline = outline;
              data.concept = concept;
              data.beatId = beatId; // the target beatId
              data.type = headerCard.data.type;
            } else {
              throw new Error("Couldn't find a group for outline card", ids[0]);
            }

            break;
          case TEXT_CARD_TYPE:
            url = "/api/ai/canvasCard";
            data.prompt = card.content;
            break;
          default:
            throw new Error("Cannot generate from card with type:", card.type);
        }
      }

      // Create the new cards
      const newCardIds = [];

      let polarity = 1;
      const PADDING = 5;
      let step = 0;

      // Calculate placing the cards
      // We place first in middle, then alternate going above and below, retaining the X position
      for (let i = 0; i < n; i++) {
        const currY = y + step * (height + PADDING + 2 * CARD_PADDING + 15) * polarity;

        if (i % 2 === 0) {
          step++;
        }

        const id = addCard({
          x, //
          y: currY,
          content: "Generating",
          generated: true,
          sourceCardIds: sourceCardIds,
          generating: true,
          width,
          height,
          autoHeight: true,
          initialEditing: false,
        });

        newCardIds.push(id);

        // alternate sides
        polarity *= -1;
      }

      newCardIds.forEach((id) => {
        dispatch({ type: SELECT_ACTION, id: id });
      });

      Auth.fetchWithIdToken(url, data).then((result) => {
        if (result.success) {
          for (let i = 0; i < n; i++) {
            const id = newCardIds[i];
            const content = result.texts[i];

            if (content) {
              // guard card not existing
              dispatch({
                type: SET_DATA_ACTION,
                id: id,
                data: {
                  content: content,
                  generating: false,
                  distribute: id,
                },
              });
            }
          }
        } else {
          setToast("An error occurred generating the card.");
          deleteCards([newCardIds]);
        }
      });
    },
    [cardIdsString, groupIdsString, groups, cards]
  );

  /**
   * Adds image card
   * Format for `content` is an object:
   *   { url: url of image, prompt: prompt (either extracted or not) }
   */
  const addImageCard = useCallback(
    ({ x, y, prompt, sourceCardIds }) => {
      if (!subscriptionJSON.hasAccessToFeatures) {
        return setToast("You must have an active subscription or trial to use AI features.");
      }

      // parses image url to display in <img> tag
      // this is the format which is returned in the multiverse visualize cards
      function parseContent(content) {
        const re = /{{\s*?image_url:\s*"(.*?)"\s*}}/;
        const c = content.replace(re, (match, url) => {
          return [`<img src='${url}' />`];
        });

        const matches = content.match(re);

        let url = null;
        if (matches && matches[1]) {
          url = matches[1];
        }

        return [c, url];
      }

      const id = addCard({
        x, //
        y,
        width: 256,
        generated: true,
        sourceCardIds: sourceCardIds,
        height: 256,
        content: "Generating",
        type: "image",
        generating: true,
        initialEditing: false,
      });

      if (!id) {
        return;
      }

      Auth.streamWithIdToken(
        "/api/ai/visualizeCanvas",
        {
          prompt: prompt,
          canvasId: canvasId,
        },
        null,
        (chunk) => {
          if (chunk.error) {
            setToast("An error occurred generating the image.");
            deleteCards([id]);
          } else if (chunk.content) {
            const [c, url] = parseContent(chunk.content.text);

            dispatch({
              type: SET_DATA_ACTION,
              id: id,
              data: {
                content: {
                  url: url,
                  prompt: chunk.content.pure_prompt,
                },
                generating: false,
              },
            });
          }
        },
        null,
        (error) => {
          setToast(error?.message || "An error occurred generating the image.");
          deleteCards([id]);
        }
      );
    },
    [cardIdsString]
  );

  return { getHighestIndex, addCard, addGeneratedCard, addImageCard, addOutlineGroup };
};
